/**
 * @fileoverview "Add Activity" form for a tenant.
 * @description Appends one new activity (call, meeting, demo, note or follow-up) to a tenant's
 * history — saved entries are never edited. Split out of TenantActivityPanel so the same form can
 * also be opened on its own, from a table row's actions menu (see TenantActionModal). `onAdded`
 * runs after a successful save, and an `onCancel` adds a Cancel button beside Save.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  ACTIVITY_TYPE_KEY,
  ACTIVITY_TYPES,
} from '@/features/sales/constants/activity-types.constants'
import { useAddTenantActivity } from '@/features/sales/hooks/useAddTenantActivity'
import {
  addActivityFormSchema,
  type AddActivityFormValues,
} from '@/features/sales/schemas/add-activity-form.schema'
import Button from '@/shared/components/atoms/Button'
import Label from '@/shared/components/atoms/Label'
import Select from '@/shared/components/atoms/Select'

import type { FormEvent } from 'react'

const EMPTY_VALUES: AddActivityFormValues = { type: 'Call', details: '', scheduledAt: '' }

interface AddActivityFormProps {
  tenantId: string
  currentUserId: string
  onAdded?: () => void
  onCancel?: () => void
}

/**
 * @component
 * @param {AddActivityFormProps} props - The tenant, the author, and the save/cancel handlers.
 * @param {string} props.tenantId - The tenant the activity is recorded against.
 * @param {string} props.currentUserId - The signed-in user's sales-roster id, recorded as author.
 * @param {() => void} [props.onAdded] - Called after the activity is saved.
 * @param {() => void} [props.onCancel] - Shows a Cancel button that calls this.
 */
function AddActivityForm({ tenantId, currentUserId, onAdded, onCancel }: AddActivityFormProps) {
  const { t } = useTranslation()
  const addActivity = useAddTenantActivity(tenantId)
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AddActivityFormValues>({
    resolver: zodResolver(addActivityFormSchema),
    defaultValues: EMPTY_VALUES,
  })

  /**
   * @description Saves the new activity, then clears the form and tells the caller.
   * @param {AddActivityFormValues} values - The validated form values.
   */
  const onValid = (values: AddActivityFormValues) => {
    addActivity.mutate(
      {
        type: values.type,
        details: values.details,
        scheduledAt: values.scheduledAt || null,
        authorId: currentUserId,
      },
      {
        onSuccess: () => {
          reset(EMPTY_VALUES)
          onAdded?.()
        },
      },
    )
  }

  /**
   * @description Runs the schema validation and, if valid, calls onValid.
   * @param {FormEvent<HTMLFormElement>} event - The form submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void handleSubmit(onValid)(event)
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      data-testid="add-activity-form"
      className="flex flex-col gap-2.5 rounded-lg border border-row-border bg-surface-hover p-3"
    >
      <span className="text-[12.5px] font-bold text-ink" data-testid="add-activity-title">
        {t('sales.tenantDetail.activities.addTitle')}
      </span>
      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
        <div>
          <Label htmlFor="activityType">{t('sales.tenantDetail.activities.typeLabel')}</Label>
          <div className="mt-1.5">
            <Select
              id="activityType"
              data-testid="add-activity-type-select"
              options={ACTIVITY_TYPES.map((type) => ({
                value: type,
                label: t(`sales.tenantDetail.activities.types.${ACTIVITY_TYPE_KEY[type]}`),
              }))}
              {...register('type')}
            />
          </div>
        </div>
        <div>
          <Label htmlFor="activityScheduledAt">
            {t('sales.tenantDetail.activities.scheduledAtLabel')}
          </Label>
          <input
            id="activityScheduledAt"
            data-testid="add-activity-scheduled-at-input"
            type="datetime-local"
            className="mt-1.5 h-[38px] w-full rounded-[8px] border border-input-border bg-white px-3 text-sm font-medium text-ink focus:outline-none focus:ring-2 focus:ring-primary/20"
            {...register('scheduledAt')}
          />
        </div>
      </div>
      <div>
        <Label htmlFor="activityDetails">{t('sales.tenantDetail.activities.detailsLabel')}</Label>
        <textarea
          id="activityDetails"
          data-testid="add-activity-details-input"
          rows={2}
          placeholder={t('sales.tenantDetail.activities.detailsPlaceholder')}
          className="mt-1.5 w-full rounded-lg border border-input-border bg-white px-3 py-2 text-[13.5px] text-ink focus:outline-none focus:ring-2 focus:ring-primary/20"
          {...register('details')}
        />
        {errors.details && (
          <span
            className="mt-1 block text-[11.5px] font-medium text-danger"
            data-testid="add-activity-details-error"
          >
            {t(errors.details.message ?? '')}
          </span>
        )}
      </div>
      <div className="flex gap-2.5">
        {onCancel && (
          <div className="w-fit">
            <Button
              type="button"
              variant="secondary"
              size="md"
              onClick={onCancel}
              data-testid="add-activity-cancel-button"
            >
              {t('sales.tenantDetail.reasonNote.cancel')}
            </Button>
          </div>
        )}
        <div className="w-fit">
          <Button
            type="submit"
            size="md"
            isLoading={addActivity.isPending}
            data-testid="add-activity-submit-button"
          >
            {t('sales.tenantDetail.activities.submit')}
          </Button>
        </div>
      </div>
    </form>
  )
}

export default AddActivityForm
